import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore } from "../../store/organization";
import {ImageLibrary} from "../../types/item";
import {GET_IMAGE_LIBRARY_ITEMS} from "./queries";
import { Badge } from "../../components/Badge";
import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import Switch from "react-switch";

const client = createApolloClient();
const { getState } = organizationStore;

const buildSearch = (search: string) => [
  {
    display_name: {
      _ilike: `%${search}%`,
    },
  },
  {
    caption: {
      _ilike: `%${search}%`,
    },
  },
];

const toDataUrl = (b64?: string) => {
  if (!b64) return "";
  return b64.startsWith("data:")
      ? b64
      : `data:image/webp;base64,${b64}`;
};

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: ImageLibrary;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: ImageLibrary }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }
    return (
      <>
        {permission ? (
          <Link to={linkFunc(params)}>
            <Badge>{params.value}</Badge>
          </Link>
        ) : (
          <Badge>{params.value}</Badge>
        )}
      </>
    );
  };
};
export const ImageLibraryListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);

  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  const { data } = await client.query({
    query: GET_IMAGE_LIBRARY_ITEMS,
    variables: {
      where,
    },
  });
  return data.image_library;
};

export const ImageLibraryList = () => {
  const { editCreatePermission } = usePermissions("image_library");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const image_library = useLoaderData() as ImageLibrary[];
  const [rowData, setRowData] = useState<ImageLibrary[]>([]);
  const [isActiveToggle, setIsActiveToggle] = useState(true);
  useEffect(() => {
    const filteredItems = isActiveToggle
        ? image_library.filter((image_library) => image_library.is_active)
        : image_library;
    setRowData(filteredItems);
  }, [image_library, isActiveToggle]);
  const generateColDefs = (): ColDef<ImageLibrary>[] => [
    {
      headerName: "Id",
      valueGetter: (p) => p.data?.id,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/image-library/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Display Name",
      valueGetter: (p) => p.data?.display_name,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/image-library/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Preview",
      field: "image_base64",
      valueGetter: () => "",
      cellRenderer: (p: any) => {
        const src = toDataUrl(p.data?.image_base64);
        if (!src) return null;

        const img = (
            <img
                src={src}
                alt={p.data?.display_name || "preview"}
                width={100}
                height={100}
                loading="lazy"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  display: "block",
                }}
            />
        );

        return editCreatePermission
            ? <Link to={`/image-library/${p.data.id}/edit`}>{img}</Link>
            : img;
      },
      width: 140,
      pinned: "left",
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
    },
    {
      headerName: "Is Active?",
      valueGetter: (p) => p.data?.is_active,
      valueFormatter: (p) => {
        return p.data?.is_active ? "Yes" : "No";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => (p.data?.is_active ? "Yes" : "No"),
    },
    {
      headerName: "Caption",
      valueGetter: (p) => p.data?.caption,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.user?.username,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];
  const colDefs: ColDef<ImageLibrary>[] = generateColDefs();
  const rowHeight = 105;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `ImageLibrary_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs/>
          <div className="col-span-1 col-start-4 mt-3 flex flex-col items-center">
            <span className="block text-sm font-medium text-gray-500">Active Only</span>
            <Switch
                checked={isActiveToggle}
                onChange={() => setIsActiveToggle((prev) => !prev)}
                onColor="#EE2E30"
                onHandleColor="#FFFFFF"
                handleDiameter={18}
                uncheckedIcon={false}
                checkedIcon={false}
                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                height={15}
                width={48}
            />
          </div>
          <div className="col-span-2 col-start-5 mt-2">
            <Input
                name="search"
                placeholder={`Search Image Library`}
                onChange={(s) => {
                  navigate(
                      s && typeof s === "string" && s.length > 0
                          ? `/image-library/list?search=${s}`
                          : `/image-library/list`
                  );
                  revalidator.revalidate();
                }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
                <PrimaryButton onClick={() => navigate(`/image-library/create`)}>
                  Create
                </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            gridOptions={gridOptions}
            paginationPageSize={100}
            onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
